/**
 * Whiteboard API
 * static/js/whiteboard/api.js
 * 
 * API 通訊封裝
 */

import { API_ENDPOINTS, MESSAGES } from './config.js';

/**
 * 發送 API 請求
 * @param {string} url 
 * @param {Object} options 
 * @returns {Promise<Object>}
 */
async function request(url, options = {}) {
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
  };

  const response = await fetch(url, { ...defaultOptions, ...options });
  
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || MESSAGES.NETWORK_ERROR);
  }
  
  return response.json();
}

/**
 * 列出用戶的白板
 * @param {boolean} includeDrafts 
 * @returns {Promise<Object>}
 */
export async function listWhiteboards(includeDrafts = false) {
  const url = `${API_ENDPOINTS.LIST}?include_drafts=${includeDrafts}`;
  return request(url);
}

/**
 * 建立新白板
 * @param {Object} data 
 * @returns {Promise<Object>}
 */
export async function createWhiteboard(data) {
  return request(API_ENDPOINTS.CREATE, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * 取得單一白板
 * @param {number} id 
 * @returns {Promise<Object>}
 */
export async function getWhiteboard(id) {
  return request(API_ENDPOINTS.GET(id));
}

/**
 * 更新白板
 * @param {number} id 
 * @param {Object} data 
 * @returns {Promise<Object>}
 */
export async function updateWhiteboard(id, data) {
  return request(API_ENDPOINTS.UPDATE(id), {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * 刪除白板
 * @param {number} id 
 * @returns {Promise<Object>}
 */
export async function deleteWhiteboard(id) {
  return request(API_ENDPOINTS.DELETE(id), {
    method: 'DELETE',
  });
}

/**
 * 取得或建立草稿
 * @returns {Promise<Object>}
 */
export async function getOrCreateDraft() {
  return request(API_ENDPOINTS.DRAFT);
}

/**
 * 自動儲存草稿（輕量級）
 * @param {number} id 
 * @param {string} sceneJson 
 * @returns {Promise<Object>}
 */
export async function saveDraft(id, sceneJson) {
  return request(API_ENDPOINTS.SAVE_DRAFT(id), {
    method: 'PUT',
    body: JSON.stringify({ scene_json: sceneJson }),
  });
}

/**
 * 將草稿提升為正式儲存
 * @param {number} id 
 * @param {string} title 
 * @param {string} thumbnailBase64 
 * @returns {Promise<Object>}
 */
export async function promoteDraft(id, title, thumbnailBase64 = null) {
  const data = { title };
  if (thumbnailBase64) {
    data.thumbnail_base64 = thumbnailBase64;
  }
  return request(API_ENDPOINTS.PROMOTE(id), {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * 匯入白板
 * @param {Object} data 
 * @returns {Promise<Object>}
 */
export async function importWhiteboard(data) {
  return request(API_ENDPOINTS.IMPORT, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}
